import React, { useMemo } from 'react';
import Hero from '@loomstateorg/shared-frontend/components/commercetools-ui/organisms/content/hero';
import useSanityPermissionsChecker from 'helpers/hooks/useSanityPermissionsChecker';
import { useFormat } from '@loomstateorg/shared-frontend/hooks/useFormat';
import { Reference } from 'types/reference';

interface Props {
  data: any;
  customerGroup: any;
}

/** Maps Sanity CTA/link data to the Reference type expected by Hero and Link. */
function getCtaReference(ctaData: any): Reference | undefined {
  if (!ctaData) return undefined;
  // Already in Reference shape (e.g. from Frontastic)
  if (ctaData.type === 'link' && ctaData.link) return ctaData as Reference;
  if (ctaData.type === 'page-folder' && ctaData.pageFolder) return ctaData as Reference;
  // Sanity: resolved link document with linkUrl / link / slug
  const url =
    ctaData.linkUrl ??
    ctaData.link ??
    (typeof ctaData.slug === 'string' ? ctaData.slug : ctaData.slug?.current);
  if (!url) return undefined;
  return {
    type: 'link',
    link: url,
    openInNewWindow: !!ctaData.openInNewTab,
  };
}

/** Extract URL from Sanity / Frontastic file or image asset shapes */
function getMediaFileUrl(media: unknown): string | undefined {
  if (!media) return undefined;
  if (typeof media === 'string') return media;

  const asset = media as Record<string, unknown>;
  if (typeof asset.url === 'string' && asset.url) return asset.url;

  const nestedAsset = asset.asset as Record<string, unknown> | undefined;
  if (nestedAsset && typeof nestedAsset.url === 'string' && nestedAsset.url) return nestedAsset.url;

  const fields = asset.fields as Record<string, unknown> | undefined;
  const file = fields?.file as Record<string, unknown> | undefined;
  if (file && typeof file.url === 'string' && file.url) return file.url;

  return undefined;
}

function mimeTypeFromUrl(url: string): string {
  if (/\.webm(\?|#|$)/i.test(url)) return 'video/webm';
  if (/\.mp4(\?|#|$)/i.test(url)) return 'video/mp4';
  return 'video/mp4';
}

/** Extract MIME type from Sanity / Frontastic file asset shapes */
function getMediaMimeType(media: unknown, url?: string): string | undefined {
  if (media && typeof media === 'object') {
    const asset = media as Record<string, unknown>;
    const nestedAsset = asset.asset as Record<string, unknown> | undefined;
    if (typeof nestedAsset?.mimeType === 'string' && nestedAsset.mimeType) {
      return nestedAsset.mimeType;
    }

    const fields = asset.fields as Record<string, unknown> | undefined;
    const file = fields?.file as Record<string, unknown> | undefined;
    if (typeof file?.mimeType === 'string' && file.mimeType) {
      return file.mimeType;
    }
  }

  return url ? mimeTypeFromUrl(url) : undefined;
}

const ComposedHeroTastic = ({ data, customerGroup }: Props) => {
  const { checkNestedItemPermission } = useSanityPermissionsChecker();
  const { formatMessage } = useFormat({ name: 'common' });

  const fields = data?.cmsData?.dataSource?.fields;
  const components = fields?.components || [];

  const filteredHeroes = useMemo(
    () => components.filter((hero: any) => checkNestedItemPermission(hero, customerGroup)),
    [components, customerGroup, checkNestedItemPermission],
  );

  if (!filteredHeroes.length) {
    return null;
  }

  const isSingleHero = filteredHeroes.length === 1;

  return (
    <section
      data-testid="hero"
      aria-label={formatMessage({ id: 'hero', defaultMessage: 'Hero' })}
      className={`grid ${isSingleHero ? 'grid-cols-1' : 'grid-cols-1 gap-4 lg:grid-cols-2'}`}
    >
      {filteredHeroes.map((hero: any, index: number) => {
        const tile = hero?.fields ?? hero;
        const videoMedia = tile?.video ?? tile?.videoDesktop;
        const videoUrl = getMediaFileUrl(videoMedia);
        const videoMimeType = getMediaMimeType(videoMedia, videoUrl);
        const imageUrl = videoUrl ? undefined : getMediaFileUrl(tile?.image);
        const ctaReference = getCtaReference(tile?.ctaReference);

        return (
          <div key={hero?._id || hero?._system?.base?.id || index} className="col-span-1">
            <Hero
              image={imageUrl}
              videoUrl={videoUrl}
              videoMimeType={videoMimeType}
              title={tile?.title}
              titleColor={tile?.titleColor}
              titleFontSize={tile?.titleFontSize}
              subtitle={tile?.subtitle}
              subtitleColor={tile?.subtitleColor}
              subtitleFontSize={tile?.subtitleFontSize}
              ctaLabel={tile?.ctaLabel}
              ctaReference={ctaReference}
              buttonBackgroundColor={tile?.buttonBackgroundColor}
              buttonBackgroundColorHover={tile?.buttonBackgroundColorHover}
              buttonBackgroundTransparent={!!tile?.buttonBackgroundTransparent}
              buttonBorderRadius={tile?.buttonBorderRadius}
              buttonHorizontalAlign={tile?.buttonHorizontalAlign}
              buttonVerticalAlign={tile?.buttonVerticalAlign}
              buttonTextColor={tile?.buttonTextColor}
              buttonFontSize={tile?.buttonFontSize}
              contentDirection={tile?.contentDirection || 'row'}
            />
          </div>
        );
      })}
    </section>
  );
};

export default ComposedHeroTastic;
